import React, { useEffect, useRef } from "react";
import { Button, Popper, Box, ClickAwayListener, useMediaQuery, Modal } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import LoginForm from "@/components/auth/login/login-form";
import { prefetchLoginBlock } from "@/hooks/login/useLoginBlock";
import CloseIcon from "@mui/icons-material/Close";
import { Theme } from "@mui/material/styles";

const LoggedOutNavItem = ({
  mappedData,
  anchorEl,
  popoverType,
  handleClick,
  handleClose,
  isScrolled = false
}: {
  mappedData: any;
  anchorEl: HTMLButtonElement | null;
  popoverType: "menu" | "login" | null;
  handleClick: (event: React.MouseEvent<HTMLButtonElement>, type: "menu" | "login") => void;
  handleClose: () => void;
  isScrolled?: boolean;
}) => {
  const queryClient = useQueryClient();
  const arrowRef = useRef<HTMLDivElement>(null);

  // Detect if the screen size is mobile
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // Prefetch the login block data when the component mounts
  useEffect(() => {
    prefetchLoginBlock(queryClient);
    if (typeof window !== "undefined") {
      // Use sessionStorage instead of localStorage for consistency with our other changes
      sessionStorage.removeItem("originalSiteOrigin");
    }
  }, [queryClient]);

  return (
    <>
      <Button
        id="nav-login-button"
        data-testid="login-button"
        sx={{
          color: "common.white",
          textTransform: "none",
          fontSize: "1.3rem",
          fontWeight: "bold",
          backgroundColor: isMobile ? "transparent" : "transparent", // Remove background color on mobile
          "&:hover": {
            backgroundColor: isMobile ? "transparent" : "primary.dark" // Remove hover background color on mobile
          }

        }}
        size="large"
        aria-describedby="login-popper"
        variant="contained"
        disableElevation
        onClick={(event) => handleClick(event, "login")}
      //  onMouseEnter={() => prefetchLoginBlock(queryClient)}
      >
        {mappedData.login}
      </Button>

      {isMobile ? (
        // Render a Modal for mobile
        <Modal
          open={popoverType === "login"}
          onClose={handleClose}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            paddingTop: "5rem",
            justifyContent: "center"
          }}
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: "common.black", // Set the backdrop color to white
                opacity: 1 // Remove transparency
              }
            }
          }}
        >
          <Box
            sx={{
              position: "relative",
              backgroundColor: "background.default",
              padding: "1rem",
              display: "flex",
              flexDirection: "column",
              borderRadius: "8px"
            }}
          >
            {/* Close Button */}
            <Button
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: "1rem",
                right: "1rem",
                fontSize: "1.2rem",
                fontWeight: "bold",
                color: "text.primary"
              }}
            >
              <CloseIcon fontSize="medium" />
            </Button>

            {/* Login Form */}
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                paddingTop: "5rem"
              }}
            >
              <LoginForm data-testid="login-form" />
            </Box>
          </Box>
        </Modal>
      ) : (
        // Render the Popper for larger screens
        <Popper
          id="login-popper"
          data-testid="login-popper"
          open={popoverType === "login"}
          anchorEl={anchorEl}
          placement="bottom-end"
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [150, isScrolled ? 80 : 0]
              }
            },
            {
              name: "arrow",
              enabled: true,
              options: {
                element: arrowRef.current
              }
            }
          ]}
          sx={{
            zIndex: 1300
          }}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Box sx={{ position: "relative" }}>
              {/* Arrow */}
              <Box
                ref={arrowRef}
                sx={{
                  position: "absolute",
                  top: "-8px", // Place the arrow above the Popper
                  left: "calc(50% - 8px)", // Center the arrow horizontally
                  width: 0,
                  height: 0,
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderBottom: "8px solid var(--theme-palette-common-white)" // Match the Popper background color
                }}
              />
              {/* Login Form */}
              <Box
                sx={{
                  backgroundColor: "background.default",
                  boxShadow: 3,
                  borderRadius: "8px",
                  padding: "1rem",
                  minWidth: "300px"
                }}
              >
                <LoginForm data-testid="login-form-mobile" />
              </Box>
            </Box>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};

export default LoggedOutNavItem;